import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Lightbulb, Loader2, Settings, Sparkles } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Textarea } from "@/shared/components/ui/textarea";
import { useToast } from "@/shared/components/ui/use-toast";
import { DataPagination } from "@/shared/components/DataPagination";
import { usePagination } from "@/shared/hooks/usePagination";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { LearningsService } from "../services/learningsService";
import { LEARNING_STATUSES, type Learning, type LearningsTarget, type LearningStatus } from "../types/LearningTypes";
import { LEARNING_CONTENT_MAX, learningsQueryKey } from "../utils/learningsView";
import { LearningCard } from "./LearningCard";
import { LearningReferencesSheet } from "./LearningReferencesSheet";
import { LearningsWorkspaceSettings } from "./LearningsWorkspaceSettings";

interface LearningsPanelProps {
  target: LearningsTarget;
  /** Admin do workspace: dispara a revisão na hora e muda as configurações dela. */
  canReview?: boolean;
}

/**
 * A caixa Learnings (AB#87286): as propostas de melhoria do Brain que o Prodgy tirou do
 * uso, com aceitar, editar e aceitar, e recusar. A mesma tela no Brain (workspace) e no
 * My view (pessoal); quem decide cada proposta vem da api.
 */
export const LearningsPanel = ({ target, canReview = false }: LearningsPanelProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<LearningStatus>("accepted");
  const { data: learnings = [], isLoading, isError } = useQuery({
    queryKey: [...learningsQueryKey(target), status],
    queryFn: () => LearningsService.list(target, status),
  });
  const { currentPage, pageSize, setCurrentPage, setPageSize, paginateData } = usePagination<Learning>();
  useEffect(() => setCurrentPage(1), [status, setCurrentPage]);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Learning | null>(null);
  const [draft, setDraft] = useState("");
  const [referencesOf, setReferencesOf] = useState<Learning | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["learnings"] });
    // A revisão na hora muda a "última revisão" das configurações.
    await queryClient.invalidateQueries({ queryKey: ["learnings-settings"] });
    // Aceitar grava na Memory ou na Mind: as telas delas atualizam também.
    await queryClient.invalidateQueries({ queryKey: ["memories"] });
  };

  const decide = async (learning: Learning, run: () => Promise<Learning>, doneKey: string) => {
    setBusyId(learning.id);
    try {
      await run();
      toast({
        title: t(doneKey),
      });
      setEditing(null);
      await refresh();
    } catch (error) {
      toast({
        title: t("learnings.toast.error"),
        description: resolveApiErrorMessage(error, t),
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  const reviewNow = async () => {
    if (!target.productId) return;
    setReviewing(true);
    try {
      const result = await LearningsService.reviewNow(target.productId);
      toast({
        title: t("learnings.review.done", { proposals: result.proposals, messages: result.messages }),
      });
      await refresh();
    } catch (error) {
      toast({
        title: t("learnings.review.error"),
        description: resolveApiErrorMessage(error, t),
        variant: "destructive",
      });
    } finally {
      setReviewing(false);
    }
  };

  return (
    <Card>
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Tabs value={status} onValueChange={(value) => setStatus(value as LearningStatus)}>
            <TabsList>
              {LEARNING_STATUSES.map((item) => (
                <TabsTrigger key={item} value={item}>
                  {t(`learnings.status.${item}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {canReview && target.productId && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => void reviewNow()} disabled={reviewing}>
                {reviewing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                {t("learnings.review.now")}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSettingsOpen(true)}
                aria-label={t("learnings.settings.open")}
                title={t("learnings.settings.open")}
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex h-[calc(100vh-280px)] items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <p className="py-6 text-sm text-destructive">{t("learnings.loadError")}</p>
        ) : learnings.length === 0 ? (
          // A mesma altura da Mind e da Memory: a área ocupa a tela, com ou sem propostas.
          <div className="flex h-[calc(100vh-280px)] flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed">
            <Lightbulb className="h-16 w-16 text-muted-foreground/40" />
            <p className="max-w-md text-center text-sm text-muted-foreground">
              {t(status === "accepted" ? `learnings.empty.${target.view}` : `learnings.emptyStatus.${status}`)}
            </p>
          </div>
        ) : (
          <>
            <ul className="min-h-[calc(100vh-340px)] space-y-3">
              {paginateData(learnings).map((learning) => (
                <LearningCard
                  key={learning.id}
                  learning={learning}
                  busy={busyId === learning.id}
                  onAccept={() =>
                    void decide(learning, () => LearningsService.accept(learning.id), "learnings.toast.accepted")
                  }
                  onEdit={() => {
                    setEditing(learning);
                    setDraft(learning.content ?? "");
                  }}
                  onReject={() =>
                    void decide(learning, () => LearningsService.reject(learning.id), "learnings.toast.rejected")
                  }
                  onRemove={() =>
                    void decide(learning, () => LearningsService.remove(learning.id), "learnings.toast.removed")
                  }
                  onReferences={() => setReferencesOf(learning)}
                />
              ))}
            </ul>
            <DataPagination
              currentPage={currentPage}
              pageSize={pageSize}
              totalItems={learnings.length}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(open) => !open && !busyId && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("learnings.edit.title")}</DialogTitle>
            <DialogDescription>{t("learnings.edit.description")}</DialogDescription>
          </DialogHeader>
          <Textarea value={draft} onChange={(event) => setDraft(event.target.value)} rows={10} maxLength={LEARNING_CONTENT_MAX} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)} disabled={!!busyId}>
              {t("learnings.edit.cancel")}
            </Button>
            <Button
              disabled={!!busyId || !draft.trim()}
              onClick={() =>
                editing &&
                void decide(
                  editing,
                  () => LearningsService.accept(editing.id, { content: draft.trim() }),
                  "learnings.toast.accepted",
                )
              }
            >
              {busyId ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("learnings.edit.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {canReview && target.productId && (
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t("learnings.settings.title")}</DialogTitle>
              <DialogDescription>{t("learnings.settings.description")}</DialogDescription>
            </DialogHeader>
            <LearningsWorkspaceSettings workspaceId={target.productId} />
          </DialogContent>
        </Dialog>
      )}

      <LearningReferencesSheet learning={referencesOf} onOpenChange={(open) => !open && setReferencesOf(null)} />
    </Card>
  );
};
